import React, { useState, useEffect } from "react";
import { getFirestore, collection, query, where, getDocs, doc, getDoc, updateDoc, addDoc, onSnapshot } from "firebase/firestore"; 
import { useNavigate } from "react-router-dom";
import { auth } from "../../Config/Config";
import Modal from "../../components/popups/Modal";
import TransactionDetailsCard from "../componets/Essentials/TransactionDetailsCard";

const Withdraws = () => {
  const [walletAddress, setWalletAddress] = useState("");
  const [amount, setAmount] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);
  const [user, setUser] = useState(null);
  const [accountBalance, setAccountBalance] = useState(0);
  const db = getFirestore();
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [withdrawId, setWithdrawId] = useState(null);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user) {
        setUser(user);
        fetchAccountBalance(user.uid);
      } else {
        navigate("/login");
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  const fetchAccountBalance = async (userId) => {
    try {
      const accountRef = doc(db, "AccountBalance", userId);
      const accountSnap = await getDoc(accountRef);

      if (accountSnap.exists()) {
        const { TotalAccountBalance } = accountSnap.data();
        setAccountBalance(TotalAccountBalance);
      } else {
        setError("Account balance not found.");
      }
    } catch (error) {
      setError("Failed to fetch account balance: " + error.message);
    }
  };

  const validateForm = () => {
    if (!walletAddress || !amount) {
      setError("Please fill in all fields.");
      return false;
    }
    if (parseFloat(amount) < 100) {
      setError("The minimum amount to withdraw is $100.");
      return false;
    }
    if (parseFloat(amount) > accountBalance) {
      setError("Insufficient account balance.");
      return false;
    }
    return true;
  };

  const handleProceed = async () => {
    setError("");
    setSuccess("");
    setLoading(true);

    if (!user) {
      setError("User is not authenticated.");
      setLoading(false);
      return;
    }

    if (!validateForm()) {
      setLoading(false);
      return;
    }

    const userId = user.uid;

    try {
      const userQuery = query(collection(db, "users"), where("uid", "==", userId));
      const userSnapshot = await getDocs(userQuery);

      if (userSnapshot.empty) {
        setError("User details not found.");
        setLoading(false);
        return;
      }

      const userData = userSnapshot.docs[0].data();
      const { firstName, lastName } = userData;

      const withdrawRef = query(collection(db, "withdraws"), where("uid", "==", userId));
      const withdrawRefSnapshot = await getDocs(withdrawRef);
      let withdrawDocId;

      if (!withdrawRefSnapshot.empty) {
        withdrawDocId = withdrawRefSnapshot.docs[0].id;
        await updateDoc(doc(db, "withdraws", withdrawDocId), {
          amountWithdraw: parseFloat(amount),
          status: "pending",
          firstName,
          lastName,
          uid: userId,
          WalletAddress: walletAddress,
          timestamp: new Date(),
        });
      } else {
        const newWithdrawDoc = await addDoc(collection(db, "withdraws"), {
          amountWithdraw: parseFloat(amount),
          status: "pending",
          Totalwithdraw: 0,
          firstName,
          lastName,
          uid: userId,
          WalletAddress: walletAddress,
          timestamp: new Date(),
        });
        withdrawDocId = newWithdrawDoc.id;
      }

      setWithdrawId(withdrawDocId);
      setSuccess("Withdrawal request submitted successfully. Please allow time for processing.");
      setIsModalOpen(true);

      const withdrawDocRef = doc(db, "withdraws", withdrawDocId);
      const unsubscribe = onSnapshot(withdrawDocRef, async (doc) => {
        const data = doc.data();
        if (data && data.status === "approved") {
          await deductFromAccountBalance(userId, parseFloat(amount));
          unsubscribe();
        }
      });

    } catch (error) {
      setError("An error occurred: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const deductFromAccountBalance = async (userId, amount) => {
    try {
      const accountRef = doc(db, "AccountBalance", userId);
      const accountSnap = await getDoc(accountRef);

      if (accountSnap.exists()) {
        const { TotalAccountBalance } = accountSnap.data();
        const newBalance = TotalAccountBalance - amount;

        await updateDoc(accountRef, {
          TotalAccountBalance: newBalance,
        });
        setAccountBalance(newBalance);
      } else {
        setError("Account balance not found.");
      }
    } catch (error) {
      setError("An error occurred while deducting the balance: " + error.message);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSuccess("");
  };

  return (
    <div className="min-h-screen bg-[#000300] pb-20 pt-12">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header Section */}
        <div className="text-center mb-16">
          <p className="text-lg font-light text-white/70 mb-2">SECURE TRANSFER PORTAL</p>
          <h1 className="text-5xl font-medium text-white tracking-wide mb-4">
            <span className="bg-gradient-to-r from-white to-white/70 bg-clip-text text-transparent">Withdraw</span> Funds
          </h1>
          <div className="w-24 h-[2px] bg-gradient-to-r from-transparent via-white to-transparent mx-auto mb-6"></div>
          <p className="text-white/60 font-light max-w-2xl mx-auto text-xl">
            Transfer your earnings to your preferred wallet
          </p>
        </div>

        {/* Main Content */}
        <div className="flex justify-center">
          <div className="w-full max-w-2xl">
            {user ? (
              <>
                <div className="space-y-8">
                  <TransactionDetailsCard />

                  {/* Withdrawal Card */}
                  <div className="bg-[#000300] p-8 rounded-xl border border-white/20 shadow-2xl relative overflow-hidden">
                    <div className="absolute inset-0 bg-[linear-gradient(45deg,transparent_25%,rgba(255,255,255,0.05)_50%,transparent_75%)] bg-[length:3px_3px] opacity-20"></div>
                    <div className="relative space-y-6">
                      <div>
                        <h2 className="text-2xl font-medium text-white">Withdrawal Details</h2>
                      </div>

                      <div>
                        <label className="block text-white/70 font-medium mb-2">Bitcoin Wallet Address</label>
                        <input
                          type="text"
                          placeholder="Enter bitcoin wallet address"
                          value={walletAddress}
                          onChange={(e) => setWalletAddress(e.target.value)}
                          className="w-full bg-[#000300] border border-white/20 rounded-lg px-4 py-3 text-white focus:ring-1 focus:ring-white focus:border-transparent placeholder-white/30"
                        />
                      </div>

                      <div>
                        <label className="block text-white/70 font-medium mb-2">Amount (USD)</label>
                        <div className="relative">
                          <span className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-white/50">$</span>
                          <input
                            type="number"
                            placeholder="100.00"
                            value={amount}
                            onChange={(e) => setAmount(e.target.value)}
                            className="w-full pl-8 bg-[#000300] border border-white/20 rounded-lg px-4 py-3 text-white focus:ring-1 focus:ring-white focus:border-transparent placeholder-white/30"
                            min="100"
                            step="0.01"
                          />
                        </div>
                        <p className="mt-2 text-sm text-white/50">
                          Minimum withdrawal: $100.00 | Available balance: ${accountBalance.toFixed(2)}
                        </p>
                      </div>

                      {error && (
                        <div className="bg-red-900/20 border-l-4 border-red-500 p-4 rounded-lg">
                          <div className="flex items-start">
                            <div className="flex-shrink-0">
                              <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                              </svg>
                            </div>
                            <div className="ml-3">
                              <p className="text-sm text-red-300">{error}</p>
                            </div>
                          </div>
                        </div>
                      )}

                      <button
                        onClick={handleProceed}
                        disabled={loading}
                        className={`w-full flex justify-center py-4 px-6 rounded-lg shadow-lg font-medium relative overflow-hidden border border-white/10 hover:border-white/20 transition-all duration-300 ${
                          loading ? "bg-white/10 cursor-not-allowed" : "bg-gradient-to-r from-[#00C853] to-[#00E676] hover:from-[#00E676] hover:to-[#00C853]"
                        }`}
                      >
                        <span className="relative z-10 text-white">
                          {loading ? (
                            <>
                              <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                              Processing...
                            </>
                          ) : (
                            "Request Withdrawal"
                          )}
                        </span>
                        <span className="absolute inset-0 bg-[linear-gradient(90deg,transparent_0%,rgba(255,255,255,0.2)_50%,transparent_100%)] opacity-0 hover:opacity-100 transition-opacity duration-300"></span>
                      </button>
                    </div>
                    <div className="relative mt-6 pt-6 border-t border-white/10">
                      <p className="text-sm text-white/60">
                        Withdrawals are processed within 24-48 hours. A 5% processing fee may apply.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Modal */}
                 {isModalOpen && (
  <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50 backdrop-blur-sm">
    <div className="bg-[#000300] rounded-xl max-w-md w-full border border-white/10 overflow-hidden shadow-2xl">
      <div className="h-[2px] bg-white"></div>
      <div className="p-8">
        <div className="text-center">
          <div className="mx-auto flex items-center justify-center h-14 w-14 rounded-full bg-white/10 mb-4">
            <svg className="h-7 w-7 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h3 className="text-2xl font-medium text-white mb-2">Request Submitted</h3>
          <p className="text-white/70 mb-6">{success}</p>
          <p className="text-white/70 mb-6">Transaction ID: {withdrawId}</p>
          <button
            type="button"
            onClick={handleCloseModal}
            className="w-full bg-white hover:bg-white/90 text-[#000300] py-3 px-6 rounded-lg transition-all duration-300 font-medium"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  </div>
)}
              </>
            ) : (
              <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#00C853]"></div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Withdraws;